import React, { useState, useEffect } from 'react';
import '../css/OrderedCarDropdown.css';
import { FaFileAlt, FaClipboardCheck,FaShippingFast , FaHandshake, FaCheckDouble, FaCheck, FaDownload } from 'react-icons/fa';
import { FaBarsProgress } from "react-icons/fa6";
import { vehiclesAPI } from '../api/api';

const labelToIcon = {
  'Megrendelés': <FaFileAlt />,
  'Gyártás': <FaBarsProgress />,
  'Szállítás': <FaShippingFast />,
  'Átadás': <FaHandshake />,
};

const OrderedCarDropdown = ({ car }) => {
  const [documents, setDocuments] = useState(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  useEffect(() => {
    const loadDocuments = async () => {
      if (!car || !car.id) return;

      try {
        setLoadingDocuments(true);
        const docData = await vehiclesAPI.getVehicleDocuments(car.id);
        setDocuments(docData.sections);
      } catch (error) {
        console.error('Error loading documents:', error);
        setDocuments({});
      } finally {
        setLoadingDocuments(false);
      }
    };

    loadDocuments();
  }, [car?.id]);

  const handleDownload = (e, file) => {
    e.preventDefault();

    try {
      if (!file.content) {
        alert('A fájl tartalma nem érhető el');
        return;
      }

      // Decode Base64 content
      const binaryString = window.atob(file.content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create blob and download
      const blob = new Blob([bytes], { type: file.mime || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.extension ? `${file.filename}.${file.extension}` : file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      alert('Hiba a fájl letöltésekor');
    }
  };

  if (!car) return null;

  // Default progress steps based on the design
  const defaultProgressSteps = [
    { icon: <FaFileAlt />, label: 'Megrendelés', done: true },
    { icon: <FaBarsProgress />, label: 'Gyártás', done: false },
    { icon: <FaShippingFast />, label: 'Szállítás', done: false },
    { icon: <FaHandshake />, label: 'Átadás', done: false },
  ];

  // Use IVS progress data if available, otherwise use default
  let progressSteps = defaultProgressSteps;

  if (car.ivs_progress && car.ivs_progress.steps && Array.isArray(car.ivs_progress.steps)) {
    progressSteps = car.ivs_progress.steps.map((step) => ({
      icon: labelToIcon[step.label] || <FaFileAlt />,
      label: step.label,
      done: step.done,
    }));
  }

  return (
    <div className="ordered-dropdown-inline-content">
      <div className="ordered-dropdown-main">
        <div className="ordered-dropdown-left">
          <div className="ordered-dropdown-section">
            <h4>Adatok</h4>
            <div className="ordered-dropdown-info-grid">
              <div>Ügyfél</div><div>{car.company}</div>
              <div>Alvázszám</div><div>{car.chassis_number17}</div>
              <div>Rendszám</div><div>{car.plate_number}</div>
              <div>Típus</div><div>{car.brand}</div>
              <div>Kilométerek</div><div>{car.monthly_distance}</div>
              <div>Státusz</div><div>{car.status_info?.status_name || car.statusText || 'Nincs státusz'}</div>
              <div>Kerék típus</div><div>{car.wheelType}</div>
              <div>Kiállító</div><div>{car.issuer}</div>
              <div>Kiállító adószáma</div><div>{car.issuerTax}</div>
              <div>Kiállítási dátum</div><div>{car.issuedDate}</div>
            </div>
            <div className="ordered-dropdown-docs">
              <h5>Dokumentumok</h5>
              {loadingDocuments ? (
                <p className="ordered-dropdown-docs-loading">Betöltés...</p>
              ) : documents && Object.keys(documents).length > 0 ? (
                Object.entries(documents).map(([sectionKey, section]) => (
                  <div key={sectionKey} className="ordered-dropdown-docs-section">
                    <h6 className="ordered-dropdown-docs-section-title">
                      {section.name}
                    </h6>
                    <ul className="ordered-dropdown-docs-list">
                      {section.files.map((file) => {
                        const fullFilename = file.extension ? `${file.filename}.${file.extension}` : file.filename;
                        return (
                          <li key={file.id} className="ordered-dropdown-docs-item">
                            <span className="ordered-dropdown-docs-link">
                              {fullFilename}
                            </span>
                            <FaDownload
                              className='ordered-dropdown-docs-download-icon'
                              onClick={(e) => handleDownload(e, file)}
                              style={{cursor: 'pointer'}}
                              title="Letöltés"
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))
              ) : (
                <p className="ordered-dropdown-docs-empty">Nincs dokumentum</p>
              )}
            </div>
          </div>
        </div>
        <div className="ordered-dropdown-center">
          <div className="ordered-dropdown-progress-header">Előrehaladási napló</div>

          {/* Progress Timeline */}
          <div className="progress-timeline">
            <div className="timeline-line"></div>
            {progressSteps.map((step, i) => (
              <div key={i} className="timeline-step">
                <div className={`timeline-circle ${step.done ? 'completed' : 'pending'}`}>
                  {step.icon}
                </div>
                <div className="timeline-label">{step.label}</div>
              </div>
            ))}
          </div>

          {/* Status Information */}
          <div className="status-info-row">
            {car.ivs_progress ? (
              <>
                {car.ivs_progress.planned_production && (
                  <div className="status-info-item">
                    <div className="status-label">Tervezett gyártás</div>
                    <div className="status-value">{car.ivs_progress.planned_production}</div>
                  </div>
                )}
                {car.ivs_progress.actual_production && (
                  <div className="status-info-item">
                    <div className="status-label">Tényleges gyártás</div>
                    <div className="status-value completed">
                      {car.ivs_progress.actual_production} <FaCheck className="status-check-icon" />
                    </div>
                  </div>
                )}
                {car.ivs_progress.planned_handover && (
                  <div className="status-info-item">
                    <div className="status-label">Tervezett átadás</div>
                    <div className="status-value">{car.ivs_progress.planned_handover}</div>
                  </div>
                )}
                {!car.ivs_progress.planned_production && !car.ivs_progress.actual_production && !car.ivs_progress.planned_handover && (
                  <div className="status-info-item">
                    <div className="status-label">IVS státusz</div>
                    <div className="status-value">Kód: {car.ivs_progress.status_code}</div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="status-info-item">
                  <div className="status-label">Szerződési státusz</div>
                  <div className="status-value completed">
                    Teljesítve <FaCheck className="status-check-icon" />
                  </div>
                </div>
                <div className="status-info-item">
                  <div className="status-label">Birtoklási státusz</div>
                  <div className="status-value pending">
                    Megrendelve <FaCheckDouble className="status-check-icon" />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="ordered-dropdown-buyer-center">Módosította: {car.status_info?.changed_by_user || '---'}</div>
          <div className="ordered-dropdown-mainimg">
            <img src={car.gallery && car.gallery[0]} alt={car.model} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderedCarDropdown; 